import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { trackFailed } from '../../../../../shared-web/track';
import { ApiError } from '../../auth/api-client';
import { emberApi, type RoomLine, type RoomPageResponse } from '../api';
import { mergeLines } from './room-lines';
import { setLineReactions, withReaction } from './room-reactions';

type Setter = Dispatch<SetStateAction<RoomPageResponse | null>>;

/** A Stream message as the page's line (the fields the server put on it: seq, kind, reason, at). */
function toLine(m: Record<string, unknown>, me: string): RoomLine | null {
  const user = (m.user ?? null) as { id?: string; name?: string; avatar?: string } | null;
  const kind = m.kind as RoomLine['kind'] | undefined;
  if (!m.id || !kind || typeof m.seq !== 'number') return null;
  return {
    id: String(m.id), seq: m.seq, kind, text: String(m.text ?? ''), at: (m.at as string | undefined) ?? (m.created_at as string | undefined) ?? null, reason: (m.reason as string | null | undefined) ?? null,
    author: kind === 'human' ? (user?.name || 'Someone') : (kind === 'host' ? 'Ember' : null),
    avatar: kind === 'human' ? (user?.avatar || null) : null,
    mine: Boolean(user?.id) && user?.id === me,
  };
}

/** Ember's words to this person alone on coming back (2026-10-09), and the line they follow: what is new starts after `afterSeq`. */
export interface RoomNote { text: string; afterSeq: number }

const withLine = (lines: RoomLine[], l: RoomLine): RoomLine[] => (lines.some((x) => x.id === l.id) ? lines : [...lines, l].sort((a, b) => a.seq - b.seq));

/**
 * The room's thread, live (2026-09-29 拍板; edge stream.js): the page loads
 * from the server as it always did, then listens on the room's Stream
 * channel as the identity this person has in this one room — a line the
 * moment it lands, a line taken down the moment it goes, the close, the
 * mute, the door closing to them. Nothing is written to Stream from here:
 * a line still goes through POST /api/rooms/:id/lines, and the answer to
 * that call (or its refusal) is what the sender's page draws. When the
 * token is refused or the connection fails, the page notes it (room_live)
 * and stays as it loaded, not live — Stream is trusted by default, no
 * polling behind it (2026-09-29 拍板: a problem there is fixed there).
 *
 * `onGone` is called when this person can no longer sit in the thread — the
 * room closed or was closed to them — and the caller decides where to go.
 *
 * Typing (2026-09-30 Riley 拍板) is the one thing written from here: the
 * field's keystrokes go to Stream as this seat (`keystroke`, throttled by
 * the SDK, typing.stop on its own), and the others' typing.start / stop land
 * in `onTyping` as the names now typing — Ember never (a room is not
 * turn-taking; 2026-09-25).
 *
 * The note on coming back (2026-10-09 拍板) reaches this one person alone as
 * the custom event `room_note` — Ember's words and the line they follow —
 * into `onNote`; it is never a line in the room.
 */
export function useRoomStream(roomId: string, live: boolean, setPage: Setter, onGone: (why: 'closed' | 'removed') => void, onTyping?: (names: string[]) => void, onNote?: (note: RoomNote) => void): { keystroke: () => void; stopTyping: () => void } {
  const channelRef = useRef<{ keystroke: () => Promise<unknown>; stopTyping: () => Promise<unknown> } | null>(null);
  const keystroke = useCallback((): void => { channelRef.current?.keystroke().catch(() => { /* a dropped keystroke is nothing */ }); }, []);
  // A line goes through our API, not the SDK's sendMessage, so the SDK never sends typing.stop for it: the sender says so at once.
  const stopTyping = useCallback((): void => { channelRef.current?.stopTyping().catch(() => { /* the timeout says it soon enough */ }); }, []);
  useEffect(() => {
    if (!live) return undefined;
    let stopped = false;
    let cleanup: (() => void) | null = null;
    const typing = new Map<string, string>();
    const tellTyping = (): void => onTyping?.([...typing.values()]);

    const refetch = (): void => {
      emberApi.room(roomId)
        .then((r) => {
          if (stopped) return;
          if (!r.me.joined || r.me.left || r.me.removed) { onGone('removed'); return; }
          if (r.room.status !== 'open') { onGone('closed'); return; }
          setPage((cur) => (cur ? { ...cur, lines: mergeLines(cur.lines, r.lines), room: { ...cur.room, ...r.room }, me: { ...cur.me, ...r.me }, people: r.people, members: r.members } : cur));
        })
        .catch(() => { /* next time */ });
    };

    const gaveUp = (why: string): void => { if (!stopped) trackFailed('room_live', why, { match_id: roomId }); };

    // The read cursor (2026-10-21 Riley): only while the tab is in front — a room left open in a background tab kept marking
    // every line read, which silenced the phone's push (push-worker already_read) and showed the others a read face for a
    // line nobody read. Lines that arrive while hidden are reported once the tab comes back. Two seconds gather a host's flush
    // into one POST, as the apps do.
    let seenTimer = 0;
    let seenOwed = false;
    const seenSoon = (): void => {
      if (document.visibilityState !== 'visible') { seenOwed = true; return; }
      if (seenTimer) return;
      seenTimer = window.setTimeout(() => {
        seenTimer = 0; seenOwed = false;
        if (!stopped) emberApi.roomSeen(roomId).catch(() => { /* next time */ });
      }, 2000);
    };
    const onVisible = (): void => { if (document.visibilityState === 'visible' && seenOwed) seenSoon(); };

    (async () => {
      let door: Awaited<ReturnType<typeof emberApi.roomStreamToken>>;
      try { door = await emberApi.roomStreamToken(roomId); } catch (err) { gaveUp(err instanceof ApiError ? `token_${err.status}` : 'token_transport'); return; }
      if (stopped) return;
      const { StreamChat } = await import('stream-chat');
      if (stopped) return;
      const client = new StreamChat(door.api_key, { timeout: 8000 });
      const me = door.user_id;
      try {
        // The first token is the door's; when the SDK asks again (the 12-hour token ran out) a fresh one is fetched
        // (2026-10-21; until then a page left open past expiry reconnected to nothing)
        let handedFirst = false;
        await client.connectUser({ id: me }, async () => {
          if (!handedFirst) { handedFirst = true; return door.token; }
          return (await emberApi.roomStreamToken(roomId)).token;
        });
        const channel = client.channel(door.channel_type, door.channel_id);
        await channel.watch();
        if (stopped) { void client.disconnectUser(); return; }
        channelRef.current = channel;
        const offs = [
          channel.on('typing.start', (e) => {
            const u = e.user as { id?: string; name?: string } | undefined;
            if (!u?.id || u.id === me || u.id === 'ember') return;
            typing.set(u.id, u.name || 'Someone'); tellTyping();
          }),
          channel.on('typing.stop', (e) => {
            const id = (e.user as { id?: string } | undefined)?.id;
            if (id && typing.delete(id)) tellTyping();
          }),
          channel.on('message.new', (e) => {
            const l = e.message ? toLine(e.message as unknown as Record<string, unknown>, me) : null;
            if (!l) return;
            // A blocked member's line never lands (page.me.blocked; 2026-09-30) — the server omits it from the page the same way.
            setPage((cur) => (cur && !(l.author && cur.me.blocked?.includes(l.author)) ? { ...cur, lines: withLine(cur.lines, l) } : cur));
            if (l.kind === 'system') refetch();
            else if (!l.mine) seenSoon();
          }),
          // An emoji on a line, or one taken off (需求四, 2026-10-19): the room's event, said by the server for whoever chose it — this person's own tap included, already drawn, so applying it again changes nothing. A blocked member's never shows (the page leaves theirs out the same way).
          channel.on('room_reaction' as never, (e: unknown) => {
            const r = e as { line_id?: string; name?: string; emoji?: string | null };
            if (!r.line_id || !r.name) return;
            setPage((cur) => (cur && !cur.me.blocked?.includes(r.name as string)
              ? { ...cur, lines: setLineReactions(cur.lines, r.line_id as string, (list) => withReaction(list, r.name as string, r.emoji ?? null, cur.me.name)) } : cur));
          }),
          // Another member read further (需求三): their avatar moves down the thread. Not this person's own, not someone they blocked.
          channel.on('room_read' as never, (e: unknown) => {
            const r = e as { name?: string; read_seq?: number };
            if (!r.name || typeof r.read_seq !== 'number') return;
            setPage((cur) => (cur && r.name !== cur.me.name && !cur.me.blocked?.includes(r.name as string)
              ? { ...cur, members: cur.members.map((m) => (m.name === r.name && !m.me ? { ...m, read_seq: Math.max(m.read_seq ?? 0, r.read_seq as number) } : m)) } : cur));
          }),
          channel.on('message.deleted', (e) => {
            const id = e.message?.id;
            if (id) setPage((cur) => (cur ? { ...cur, lines: cur.lines.filter((l) => l.id !== id) } : cur));
          }),
          channel.on('channel.updated', (e) => {
            const status = (e.channel as unknown as { status?: string } | undefined)?.status;
            if (status && status !== 'open') onGone('closed');
          }),
          client.on('notification.removed_from_channel', () => onGone('removed')),
          client.on('room_removed', () => onGone('removed')),
          client.on('room_muted', (e) => {
            const until = (e as unknown as { until?: string | null }).until ?? null;
            setPage((cur) => (cur ? { ...cur, me: { ...cur.me, muted_until: until } } : cur));
          }),
          client.on('room_note', (e) => {
            const n = e as unknown as { room_id?: string; text?: string; after_seq?: number };
            if (n.room_id === roomId && n.text) onNote?.({ text: n.text, afterSeq: Number(n.after_seq) || 0 });
          }),
          client.on('connection.recovered', () => refetch()),
        ];
        document.addEventListener('visibilitychange', onVisible);
        cleanup = () => { channelRef.current = null; typing.clear(); tellTyping(); offs.forEach((o) => o.unsubscribe()); document.removeEventListener('visibilitychange', onVisible); window.clearTimeout(seenTimer); void client.disconnectUser(); };
        // What landed between the page's read and this watch is read once now (2026-10-26 walk-through: the channel is
        // only joined once through the door, and a room's first hello, said while the person stood at it, never showed;
        // the apps' watch replays the channel's lines, the web's message.new does not).
        refetch();
      } catch {
        void client.disconnectUser();
        gaveUp('connect');
      }
    })();

    return () => { stopped = true; cleanup?.(); };
  }, [roomId, live]); // eslint-disable-line react-hooks/exhaustive-deps
  return { keystroke, stopTyping };
}
